/**
 * LoginPage Component
 * Handles user login
 */

import type { FormEvent } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTurnstile } from '../hooks/useTurnstile';
import { Turnstile } from '../components/ui/Turnstile';
import { validateEmail } from '../lib/emailValidation';

export const LoginPage = () => {
  const { login, isLoggingIn, loginError } = useAuth();
  const { turnstileToken, onVerify, resetTurnstile, isTurnstileReady, turnstileResetRef } = useTurnstile();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Email validation on blur
  const handleEmailBlur = useCallback(() => {
    if (!email.trim()) {
      setEmailError(null);
      return;
    }
    const result = validateEmail(email);
    setEmailError(result.error);
  }, [email]);

  // Clear email error when user starts typing
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    // Validate email format before submitting
    const emailResult = validateEmail(email);
    if (!emailResult.isValid) {
      setEmailError(emailResult.error);
      return;
    }

    try {
      const response = await login({ email, password, turnstile_token: turnstileToken });
      // Redirect based on user role using hard redirect
      const redirectPath = hasAdminAccess(response.user.role) ? '/dashboard' : '/home';
      window.location.href = redirectPath;
    } catch (err) {
      resetTurnstile();
      setError(loginError || 'Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-app py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-heading">
            Team Wizard
          </h2>
          <p className="mt-2 text-center text-sm text-meta">
            Sign in to your account
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {(error || loginError) && (
            <div className="rounded-md bg-status-danger-subtle p-4">
              <p className="text-sm text-status-danger">{error || loginError}</p>
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`appearance-none rounded-none relative block w-full px-3 py-2 border placeholder-meta text-heading bg-surface rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm ${
                  emailError ? 'border-red-500 dark:border-red-500' : 'border-strong'
                }`}
                placeholder="Email address"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                disabled={isLoggingIn}
              />
              {emailError && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1 px-1">{emailError}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-strong placeholder-meta text-heading bg-surface rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-meta hover:text-body"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="text-right">
            <Link
              to="/reset-password"
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
            >
              Forgot password?
            </Link>
          </div>

          <Turnstile onVerify={onVerify} action="login" resetRef={turnstileResetRef} />

          <div>
            <button
              type="submit"
              disabled={isLoggingIn || !isTurnstileReady}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

        </form>
      </div>

      {/* Copyright footer - fixed to bottom */}
      <div className="fixed bottom-0 left-0 right-0 pb-4 text-sm text-center text-meta bg-surface-app">
        <a href="https://www.planetsg.com/" target="_blank" rel="noopener noreferrer" className="hover:underline">
          PLANet Systems Group
        </a>
        , Copyright 2026
      </div>
    </div>
  );
};
